import { useEffect, useState, useTransition } from 'react';

function getComputedStyleTop(element: HTMLElement): number {
  return parseInt(getComputedStyle(element).top, 10) || 0;
}

type Options = {
  sentinelMode?: boolean;
};

export function useSticky<T extends HTMLElement>({ sentinelMode }: Options = {}) {
  const [element, setElement] = useState<T | null>(null);
  const [, startTransition] = useTransition();
  const [styleTop, setStyleTop] = useState<number>(0);
  const [isSticky, setIsSticky] = useState<boolean>(false);

  useEffect(() => {
    if (!element) {
      setIsSticky(false);

      return;
    }

    if (!sentinelMode) {
      setStyleTop(getComputedStyleTop(element));
    }

    const rootMarginShift = sentinelMode ? 0 : styleTop;
    const observer = new IntersectionObserver(
      ([{ intersectionRatio }]) => {
        setIsSticky(intersectionRatio < 1);
      },
      {
        rootMargin: `${-1 - rootMarginShift}px 0px 0px 0px`,
        threshold: [1],
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [element, styleTop, sentinelMode]);

  useEffect(() => {
    if (!element || sentinelMode) {
      return;
    }

    const observer = new MutationObserver(() => {
      const computedStyleTop = getComputedStyleTop(element);

      if (computedStyleTop !== styleTop) {
        startTransition(() => {
          setStyleTop(computedStyleTop);
        });
      }
    });

    observer.observe(element, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    return () => {
      observer.disconnect();
    };
  }, [element, sentinelMode, styleTop]);

  return [setElement, isSticky] as const;
}
